import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "../../translations";

import "../styles/section-chef.css";

import { UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";

import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { POST_USER_DATA } from "../../store/action";
import { openNotificationWithIcon } from "./notification-bar";

import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { createLogger } from "../../utils/logger";

const logger = createLogger("SelectAddress");
// Metropolitan area extraction function
const extractMetropolitanArea = (addressComponents, formattedAddress = "") => {
  let city = null;
  let metropolitanArea = null;
  let province = null;
  let country = null;
  let postalTown = null;
  let sublocality = null;

  addressComponents.forEach((component) => {
    const types = component.types;
    const longName = component.long_name;

    if (types.includes("country")) {
      country = longName;
    } else if (types.includes("administrative_area_level_1")) {
      province = longName;
    } else if (types.includes("administrative_area_level_2")) {
      metropolitanArea = longName;
    } else if (types.includes("locality")) {
      city = longName;
    } else if (types.includes("postal_town")) {
      postalTown = longName;
    } else if (
      types.includes("sublocality_level_1") ||
      types.includes("sublocality")
    ) {
      sublocality = longName;
    }
  });

  // Enhanced fallback logic for Canadian addresses
  if (!metropolitanArea) {
    if (city) {
      if (country === "Canada") {
        if (city.toLowerCase().includes("vancouver")) {
          metropolitanArea = "Metro Vancouver Regional District";
        } else if (city.toLowerCase().includes("toronto")) {
          metropolitanArea = "Greater Toronto Area";
        } else if (city.toLowerCase().includes("montreal")) {
          metropolitanArea = "Greater Montreal";
        } else if (city.toLowerCase().includes("calgary")) {
          metropolitanArea = "Calgary Metropolitan Area";
        } else if (city.toLowerCase().includes("edmonton")) {
          metropolitanArea = "Edmonton Metropolitan Area";
        } else if (city.toLowerCase().includes("ottawa")) {
          metropolitanArea = "Ottawa-Gatineau Metropolitan Area";
        } else {
          metropolitanArea = `${city} Metropolitan Area`;
        }
      } else {
        metropolitanArea = city;
      }
    } else if (postalTown) {
      metropolitanArea = postalTown;
    } else if (sublocality) {
      metropolitanArea = sublocality;
    } else if (province) {
      metropolitanArea = province;
    } else {
      metropolitanArea = "Unknown";
    }
  }

  // Additional fallback: Check if we can infer from province for Canadian addresses
  if (metropolitanArea === "Unknown" && country === "Canada" && province) {
    if (province === "British Columbia" || province === "BC") {
      metropolitanArea = "Metro Vancouver Regional District";
    } else if (province === "Ontario" || province === "ON") {
      metropolitanArea = "Greater Toronto Area";
    } else if (province === "Quebec" || province === "QC") {
      metropolitanArea = "Greater Montreal";
    } else if (province === "Alberta" || province === "AB") {
      metropolitanArea = "Calgary Metropolitan Area";
    }
  }

  // Final fallback: Check formatted address for keywords
  if (metropolitanArea === "Unknown" && formattedAddress) {
    const addressLower = formattedAddress.toLowerCase();
    if (
      addressLower.includes("vancouver") ||
      addressLower.includes("bc") ||
      addressLower.includes("british columbia")
    ) {
      metropolitanArea = "Metro Vancouver Regional District";
    } else if (
      addressLower.includes("toronto") ||
      addressLower.includes("ontario")
    ) {
      metropolitanArea = "Greater Toronto Area";
    } else if (
      addressLower.includes("montreal") ||
      addressLower.includes("quebec")
    ) {
      metropolitanArea = "Greater Montreal";
    } else if (
      addressLower.includes("calgary") ||
      addressLower.includes("alberta")
    ) {
      metropolitanArea = "Calgary Metropolitan Area";
    } else if (addressLower.includes("edmonton")) {
      metropolitanArea = "Edmonton Metropolitan Area";
    } else if (addressLower.includes("ottawa")) {
      metropolitanArea = "Ottawa-Gatineau Metropolitan Area";
    }
  }

  return {
    city: city || postalTown || "Unknown",
    metropolitan_area: metropolitanArea,
    province: province || "Unknown",
    country: country || "Unknown"
  };
};

const SelectAddress = (props) => {
  const { address, setAddress, user } = props;
  const history = useHistory();

  const [load, setLoad] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const dispatch = useDispatch();

  const lang = useSelector((state) => state.lang);

  const post_user_data = (data) => {
    dispatch(post_user(data));
  };

  // Initialize address state with existing user data
  useEffect(() => {
    if (user?.street) {
      // Handle both old format { lat, lng } and new GeoJSON format [lng, lat]
      let lat, lng;
      if (
        user.position?.coordinates &&
        Array.isArray(user.position.coordinates)
      ) {
        // New GeoJSON format: [longitude, latitude]
        lng = user.position.coordinates[0];
        lat = user.position.coordinates[1];
      } else if (user.position?.lat && user.position?.lng) {
        // Old format: { lat, lng }
        lat = user.position.lat;
        lng = user.position.lng;
      }

      setAddress((prev) => ({
        ...prev,
        street: user.street,
        line: user.line || "",
        city: user.city || "",
        metropolitan_area: user.metropolitan_area || "",
        province: user.province || "",
        country: user.country || "",
        lat: lat || null,
        lng: lng || null
      }));

      // Set the selected place for the autocomplete
      setSelectedPlace({
        label: user.street,
        value: {
          place_id: "existing_address",
          formatted_address: user.street
        }
      });
    }
  }, [
    user?.street,
    user?.line,
    user?.position,
    user?.city,
    user?.metropolitan_area,
    user?.province,
    user?.country
  ]);

  const saveUpdate = async () => {
    setLoad(true);

    logger.log("🏙️ Saving chef address with metropolitan area details:", {
      street: address?.street,
      city: address?.city,
      metropolitan_area: address?.metropolitan_area,
      province: address?.province,
      country: address?.country,
      coordinates: { lat: address?.lat, lng: address?.lng }
    });

    const requestData = {
      street: address?.street,
      line: address?.line,
      city: address?.city,
      metropolitan_area: address?.metropolitan_area,
      province: address?.province,
      country: address?.country,
      position: {
        type: "Point",
        coordinates: [address?.lng, address?.lat] // GeoJSON format: [longitude, latitude]
      }
    };

    logger.log("🚀 Sending data to backend:", requestData);

    const request_details = {
      type: UPDATE,
      url: service_api,
      route: `user/update-user/${user?._id}`,
      data: requestData,
      token: user?.refresh_token
    };

    try {
      const response = await MakeRequestAsync(request_details);
      const decryptedData = decryptSingleData(response.data.user);
      post_user_data({
        ...decryptedData
      });
      if (user?.restaurant) {
        return history.push("/chef/iam-a-chef/home");
      } else {
        return history.push("/chef/iam-a-chef/hub");
      }
    } catch (err) {
      setLoad(false);
      logger.log(
        t("select_address_native.fetch_data_error"),
        err?.response?.data?.message
      );
      openNotificationWithIcon(
        "error",
        t("select_address_native.save_address_error", lang)
      );
    }
  };

  async function handleConvert(text) {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          text
        )}&key=${process.env.REACT_APP_API_KEY_MAP}&result_type=locality|administrative_area_level_2|administrative_area_level_1|country|postal_town|sublocality_level_1|sublocality`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.geometry.location;

        // Extract metropolitan area information
        const metropolitanInfo = extractMetropolitanArea(
          result.address_components,
          result.formatted_address
        );

        setAddress({
          street: text,
          lat,
          lng,
          city: metropolitanInfo.city,
          metropolitan_area: metropolitanInfo.metropolitan_area,
          province: metropolitanInfo.province,
          country: metropolitanInfo.country
        });

        logger.log("✅ Address converted with metropolitan area:", {
          street: text,
          lat,
          lng,
          metropolitan_area: metropolitanInfo
        });
      } else {
        setAddress({ street: text, lat: null, lng: null });
        logger.log("❌ Geocoding failed");
      }
    } catch (error) {
      logger.error("❌ Geocoding error:", error);
      setAddress({ street: text, lat: null, lng: null });
    }
  }

  return (
    <div className="section-chef-menu-right-address">
      <div className="section-chef-menu-right-address-inside">
        <div>
          <p
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 15,
              fontWeight: 600,
              marginTop: 30
            }}
          >
            {user?.street === null &&
              t("select_address_native.title_create", lang)}
            {user?.street !== null &&
              t("select_address_native.title_edit", lang)}
          </p>
          <p
            style={{
              textAlign: "left",
              fontSize: 18
            }}
          >
            {t("select_address_native.instruction", lang)}
          </p>

          <div className="section-chef-menu-right-address-inside-box">
            <div
              style={{
                width: "100%",
                marginTop: "20px"
              }}
            >
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_API_KEY_MAP}
                selectProps={{
                  placeholder: t("select_address_native.placeholder", lang),
                  value: selectedPlace,
                  onChange: (selectedPlace) => {
                    setSelectedPlace(selectedPlace);
                    if (selectedPlace) {
                      logger.log("🎯 Place selected:", selectedPlace.label);
                      handleConvert(selectedPlace.label);
                    }
                  },
                  styles: {
                    container: (provided) => ({
                      ...provided,
                      width: "100%"
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      padding: "4px",
                      fontSize: "16px",
                      border: `2px solid ${state.isFocused ? "#ff114d" : "#ddd"}`,
                      borderRadius: "5px",
                      outline: "none",
                      transition: "border-color 0.3s",
                      boxSizing: "border-box",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#ff114d" : "#ddd"
                      }
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: "#999"
                    })
                  }
                }}
                autocompletionRequest={{
                  componentRestrictions: { country: "ca" },
                  language: lang === "fr" ? "fr" : "en"
                }}
              />
            </div>

            {/* Address Line 2 Input */}
            <div
              style={{
                width: "100%",
                marginTop: 20
              }}
            >
              <input
                type="text"
                placeholder={t("select_address_native.placeholder_line2", lang)}
                value={address?.line || ""}
                onChange={(e) =>
                  setAddress({ ...address, line: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "16px",
                  border: "2px solid #ddd",
                  borderRadius: "5px",
                  outline: "none",
                  transition: "border-color 0.3s",
                  boxSizing: "border-box"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff114d";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#ddd";
                }}
              />
            </div>
          </div>
        </div>

        <div
          className="btn-get-started"
          style={{
            backgroundColor: "#ff114d",
            width: "100%"
          }}
          onClick={() => {
            if (load) {
              return;
            }
            if (!address?.street || address?.street?.length === 0) {
              return openNotificationWithIcon(
                "error",
                t("select_address_native.error_provide_address", lang)
              );
            }
            // Address Line 2 is now optional - removed mandatory validation
            return saveUpdate();
          }}
        >
          {!load && t("buttons.save_2", lang)}
          {load && <CircularProgress size={25} />}
        </div>
      </div>
    </div>
  );
};

export default SelectAddress;

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});
